import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { SlideoutLayout } from '@jbhi-fi/components/src/Attach/SlidoutLayout/SlideoutLayout';
import { SlideoutLayoutSection } from '@jbhi-fi/components/src/Attach/SlideoutLayoutSection/SlideoutLayoutSection';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { fetchServiceSuggestions, getSelectedServiceSuggestion } from '../pdp/utils/product-service';
import ProductService from '../pdp/productService/ProductService';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { useAttachSuggestion } from '@jbhi-fi/components/src/hooks/useAttachSuggestion/useAttachSuggestion';
import { useQuery } from 'react-query';
import { Slideout } from '@jbhi-fi/components/src/Slideout/Slideout';
import { ShopifyProductActionsProvider } from '../../../contexts/ShopifyProductActionsProvider';
import { TrackingProvider, useTrackingContext } from '@jbhi-fi/components/src/Tracking';
import { IdPrefixProvider } from '@jbhi-fi/components/src/IdPrefix';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { trackCheckoutTriggered } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackCheckoutTriggered';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { useCouponContext } from '../../../contexts/CouponContextProvider';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { EnhancedProductCarousel } from '@jbhi-fi/components/src/Attach/EnhancedProductCarousel/EnhancedProductCarousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { SmartModuleWrapper } from '../pdp/smartModules/SmartModuleWrapper';
import { styles } from './AttachAddToCartSlideout.css';
import { SmartModuleProvider, SourceProduct } from '../pdp/SmartModuleProvider';
import { useSuggestedProducts } from '@jbhi-fi/components/src/hooks/useSuggestedProducts';
import { DigitalProductBlock } from '@jbhi-fi/components/src/Attach/DigitalProductBlock/DigitalProductBlock';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { useMicrosoft365Props } from '../../../templates/components/pdp/utils/utils';
import { CollapsedContentBlock } from '@jbhi-fi/components/src/Attach/CollapsedContentBlock/CollapsedContentBlock';
import { ServiceIds } from '../../../utils/constants/services';
import { SuggestionsApiResponse } from '@jbhi-fi/apis/src/services/ServicesApiClient';
import { LineItemProps, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { InstallProductService } from '../pdp/installProductService/InstallProductService';
import { trackInstallActions } from '@jbhi-fi/utils/src/ga4Tracking';
import { InstallEventArgs } from '../pdp/installProductService/types';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformCartItemForTracking } from '../../../utils/cart/transformCartItemForTracking';

export interface AttachAddToCartSlideoutProps {
  purchasedProduct: AlgoliaProductHit;
  freeBonusProducts?: Array<AlgoliaProductHit>;
  isOpen?: boolean;
  pageType: string;
  onClose: () => void;
  shouldFetchServiceSuggestions?: boolean;
}

const getObjectPropertyValueNoCase = (obj: Record<any, any>, prop: string) =>
  Object.entries(obj).find(([key]) => key.toLowerCase() === prop.toLowerCase())?.[1];

export const AttachAddToCartSlideout = ({
  purchasedProduct,
  freeBonusProducts,
  onClose,
  pageType,
  isOpen = false,
  shouldFetchServiceSuggestions,
}: AttachAddToCartSlideoutProps) => {
  const msErrorMessageRef = useRef<HTMLDivElement>(null);
  const attachErrorMessageRef = useRef<HTMLDivElement>(null);
  const [scrollToErrorTrigger, setScrollToErrorTrigger] = useState(false);
  const [attachProductStatus, setAttachProductStatus] = useState<'added' | 'cancelled' | 'hidden'>('hidden');
  const [msProductStatus, setMsProductStatus] = useState<'added' | 'cancelled' | 'hidden'>('hidden');
  const [isMSProductAdded, setIsMSProductAdded] = useState(false);
  const [showErrorMessageAttach, setShowErrorMessageAttach] = useState(false);
  const [showErrorMessageMs, setShowErrorMessageMs] = useState(false);
  const addonSelectionRef = useRef<InstallEventArgs | undefined>(undefined);
  const attachConfig = window.Jbhifi?.attachConfig;
  const isRequiredServiceActionEnabled = attachConfig?.isRequiredServiceActionEnabled;
  const attachSlideoutModuleOrder = attachConfig?.slideoutModuleOrder ?? ['products', 'services'];

  const smartModuleSettings = window.smartModuleSettings?.productAttach;

  const purchasedProductSku = purchasedProduct.sku || purchasedProduct?.product?.id || '';

  const purchasedProductGroupId = purchasedProduct.productGroup?.id.toString() || '';
  const isMarketplaceProduct = AlgoliaHitTransformer.isMarketplaceProduct(purchasedProduct);

  const isPreOrder: boolean = purchasedProduct?.product?.productFlags?.some(flag => flag.Name === 'Pre Order') ?? false;
  const isPreOrderClickAndCollect: boolean =
    purchasedProduct?.product?.productFlags?.some(flag => flag.Name === 'Pre Order Click & Collect') ?? false;
  const isPreOrderProduct: boolean =
    purchasedProduct.availability.canPreOrder || isPreOrder || isPreOrderClickAndCollect;

  const isEnhancedGoogleRecommendationsEnabled = useFeatureFlag('isEnhancedGoogleRecommendationsEnabled');

  const isDesktop = useBreakpoint('md', 'up');
  const eSimProperty = getObjectPropertyValueNoCase(purchasedProduct.facets, 'sim type');
  const isESimOnly = Array.isArray(eSimProperty)
    ? eSimProperty.some(item => item?.toLowerCase().includes('esim only'))
    : false;

  const {
    isCouponSurfacingEnabled,
    couponsWithExclusions,
    couponOffering,
    shouldShowSpendAndGetBanner,
    currentSpend,
    isProductExcluded,
    handleSelectCoupon,
    handleUpdateShowSpendAndGetBanner,
  } = useCouponContext({
    location: 'slideout',
    product: {
      sku: purchasedProductSku,
      productGroupId: purchasedProductGroupId,
      supplierNumber: purchasedProduct.product?.supplierProductDetails?.[0]?.supplierNumber.toString() || '',
      isPreOrder: isPreOrderProduct,
      isMarketplace: isMarketplaceProduct,
      departmentCode: purchasedProduct.product?.departmentCode?.toString() || '',
    },
  });

  const offering = couponOffering
    ? {
        coupon: couponOffering,
        currentSpend,
        shouldShowSpendAndGetBanner,
        isProductExcluded,
        handleSelectCoupon,
        handleUpdateShowSpendAndGetBanner,
      }
    : undefined;

  const { isPerks } = useCustomerContext();

  const { title, type, isEnabled, googleRecModelType } = smartModuleSettings;
  const { cart } = useCartContext();
  const { tracker } = useTrackingContext();
  //TODO: This is only here because we still haven't fully decommissioned the old attach block carousel in it's appearance.
  //  When we do, we will only be using <SmartModuleWrapper /> with different variants and will not need this additional
  //  hook usage here as SmartModuleWrapper does this internally using it's provider.
  const { suggestions, isLoading: isSuggestedProductsLoading } = useSuggestedProducts({
    products: [purchasedProduct],
    googleRecNumProducts: smartModuleSettings?.productCount || 12,
    googleRecModel: smartModuleSettings?.googleRecModelType || 'FBT',
    googleRecAcceptedDeviationPercentage: window.smartModuleSettings?.googleAcceptedDeviationPercentage,
    excludedSkus: cart?.items.map(item => item.sku),
    shouldEnhanceSuggestions: !!isEnhancedGoogleRecommendationsEnabled,
    isEnabled: isDesktop || !isEnhancedGoogleRecommendationsEnabled,
    // We don't want to fetch google recs in this way if we're using SmartModuleWrapper, so when enhanced suggestions are enabled
  });
  const microsoft365AttachMessaging = window.Jbhifi.microsoftAttachMessaging;
  const attachProductGroupIds = microsoft365AttachMessaging?.productGroupIds.split(',');
  const shouldShowMicrosoft365Attach =
    microsoft365AttachMessaging?.isMicrosoftAttachEnabled &&
    attachProductGroupIds?.some(id => id === purchasedProductGroupId);
  const isMsAttachedEnabled = microsoft365AttachMessaging?.isMicrosoftAttachEnabledInAttachSlideout;
  const { products: [microsoft365Product] = [] } = useProducts({
    productSkus: [microsoft365AttachMessaging?.attachSku.toString() ?? ''],
    isEnabled: shouldShowMicrosoft365Attach && isMsAttachedEnabled,
  });
  const ms365props = useMicrosoft365Props(microsoft365Product, 'attach_slideout');

  const { attachHeading, isLoading: isAttachSuggestionLoading } = useAttachSuggestion({
    purchasedCategoryId: purchasedProduct?.category_id,
  });

  const attachHeadingText = isEnhancedGoogleRecommendationsEnabled
    ? ''
    : attachHeading ?? (window.Jbhifi?.attachConfig?.attachHeading || '');

  const variant = purchasedProduct
    ? [
        {
          Sku: parseInt(purchasedProductSku),
          DisplayPriceInc: purchasedProduct.pricing?.displayPriceInc,
          ProductGroupId: Number(purchasedProductGroupId),
        },
      ]
    : undefined;
  const { data: serviceSuggestions = [], isLoading: isServicesLoading } = useQuery({
    queryKey: ['serviceSuggestions', variant],
    queryFn: () => (variant ? fetchServiceSuggestions(variant) : undefined),
    enabled: !isMarketplaceProduct && shouldFetchServiceSuggestions,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const installSuggestions: SuggestionsApiResponse['lineItems'] = useMemo(() => {
    if (!serviceSuggestions?.length) return [];
    return serviceSuggestions.map((li: LineItemProps) => {
      const all = li.services ?? [];
      const onlyInstall = all.filter((s: ServicesProps) => s.id === ServiceIds.Install);
      return { ...li, services: onlyInstall };
    });
  }, [serviceSuggestions]);

  const nonInstallSuggestions: SuggestionsApiResponse['lineItems'] = useMemo(() => {
    if (!serviceSuggestions?.length) return [];
    return serviceSuggestions.map((li: LineItemProps) => {
      const all = li.services ?? [];
      const filtered = all.filter((s: ServicesProps) => s.id !== ServiceIds.Install);
      return { ...li, services: filtered };
    });
  }, [serviceSuggestions]);

  const hasInstall = !!installSuggestions?.[0]?.services?.length;
  const hasNonInstallServices = !!nonInstallSuggestions?.[0]?.services?.length;
  const hasServiceSuggestions = hasInstall || hasNonInstallServices;

  const isLoading = isAttachSuggestionLoading || isSuggestedProductsLoading || isServicesLoading;
  const isProductsModuleDisabled =
    attachConfig?.excludedSkus?.includes(purchasedProductSku) ||
    attachConfig?.excludedGroupIds?.includes(purchasedProductGroupId) ||
    !isEnabled;

  const hasProductsOrServices = hasServiceSuggestions || (!isProductsModuleDisabled && !!suggestions?.length);

  const cartProduct = cart?.items?.find(
    item => item.sku === purchasedProductSku && item.line_price / 100 === purchasedProduct.price
  );

  const productCartId = cartProduct?.properties?._uuid;

  const preSelectedOfferingId = getSelectedServiceSuggestion()?.selectedOfferingId ?? null;

  const currentProduct: SourceProduct = {
    sku: purchasedProductSku,
    productGroup: purchasedProductGroupId,
    optimizelyBundleOptions: [],
    bundleOptions: [],
  };

  useEffect(() => {
    if (!attachConfig.shouldShowActionButtons) return;
    const addedServiceLink = cart?.items?.find(item => item.sku === purchasedProductSku)?.properties._serviceLink;
    const isAttachProductInCart = cart?.items?.some(
      item => item.properties._isAttachedService && item.properties._serviceLink === addedServiceLink
    );

    if (ms365props.alreadyInCart && msProductStatus !== 'added') {
      setMsProductStatus('added');
      setIsMSProductAdded(false);
      setShowErrorMessageMs(false);
    } else if (!ms365props.alreadyInCart && msProductStatus === 'added') {
      setMsProductStatus('hidden');
      setIsMSProductAdded(false);
    }

    if (isAttachProductInCart && attachProductStatus !== 'added') {
      setAttachProductStatus('added');
      setShowErrorMessageAttach(false);
    } else if (!isAttachProductInCart && attachProductStatus === 'added') {
      setAttachProductStatus('hidden');
    }
  }, [
    attachConfig.shouldShowActionButtons,
    attachProductStatus,
    cart?.items,
    cart?.items.length,
    ms365props.alreadyInCart,
    msProductStatus,
    purchasedProductSku,
  ]);

  useEffect(() => {
    if (showErrorMessageMs && msErrorMessageRef.current) {
      msErrorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (showErrorMessageAttach && attachErrorMessageRef.current) {
      attachErrorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showErrorMessageAttach, showErrorMessageMs, scrollToErrorTrigger]);

  const handleAddonSelectionChange = (installEventArgs: InstallEventArgs) => {
    addonSelectionRef.current = installEventArgs;
  };

  const pushSelectedAddonEvent = async () => {
    const { event, eventType, gtmSource, addonOptions } = addonSelectionRef.current || {};
    if (!event || addonOptions?.length == 0) {
      return;
    }
    trackInstallActions({
      event,
      eventType,
      gtmSource,
      addonOptions,
    });

    cart?.items
      .filter(
        item =>
          item?.properties?._serviceType === ServiceIds.Install &&
          item?.properties?._serviceLink === cartProduct?.properties?._serviceLink
      )
      .forEach(cartItem => {
        cartItem.properties = {
          ...cartItem.properties,
          _itemListName: 'TV Installs',
          _itemListSlot: 'slideout',
        };
        trackAddToCart({
          location: 'slideout',
          currency: getStoreCurrency(),
          addedItems: transformCartItemForTracking(cartItem),
        });
      });
  };

  const areServicesActioned = () => {
    let shouldBlockProgress = false;
    if (shouldShowMicrosoft365Attach && msProductStatus === 'hidden') {
      setShowErrorMessageMs(true);
      shouldBlockProgress = true;
    }
    if (hasServiceSuggestions && attachProductStatus === 'hidden') {
      setShowErrorMessageAttach(true);
      shouldBlockProgress = true;
    }
    if (shouldBlockProgress) {
      setScrollToErrorTrigger(prevKey => !prevKey);
    }
    return !shouldBlockProgress;
  };

  const handleCheckoutClick = async () => {
    if (isRequiredServiceActionEnabled && !areServicesActioned()) {
      return;
    }
    await pushSelectedAddonEvent();
    tracker.trackClick({ label: 'Checkout' });
    trackCheckoutTriggered({ triggerSource: 'attach_slideout' });
    window.location.href = '/checkout';
  };

  const handleReviewCartClick = async () => {
    if (isRequiredServiceActionEnabled && !areServicesActioned()) {
      return;
    }
    await pushSelectedAddonEvent();
    tracker.trackClick({ label: 'Review cart' });
    window.location.href = '/cart';
  };

  const handleContinueShopping = async () => {
    await pushSelectedAddonEvent();
    tracker.trackClick({ label: 'Continue shopping' });
    onClose();
  };

  const handleServiceStatus = (serviceType: 'ms365' | 'attach') => {
    if (serviceType === 'ms365') {
      setShowErrorMessageMs(false);
      setMsProductStatus(msProductStatus === 'cancelled' ? 'hidden' : 'cancelled');
    } else {
      setShowErrorMessageAttach(false);
      setAttachProductStatus(attachProductStatus === 'cancelled' ? 'hidden' : 'cancelled');
    }
  };

  const renderErrorMessage = () => (
    <div className={styles.errorMessage}>
      <p>Please choose an option to continue</p>
    </div>
  );

  const installsModule = isServicesLoading ? (
    <LoadingSkeleton.Box height={{ xs: '320px', md: '220px' }} />
  ) : hasInstall && purchasedProductSku ? (
    <SlideoutLayoutSection key="tvInstall">
      <InstallProductService
        key={`tvinstall-${purchasedProductSku}-${isOpen}-${preSelectedOfferingId}`}
        productSku={purchasedProductSku}
        productServiceLink={cartProduct?.properties._serviceLink}
        facets={purchasedProduct?.facets}
        productCartId={productCartId}
        preSelectedOfferingId={preSelectedOfferingId}
        suggestions={installSuggestions}
        variant="slideout"
        onAddonSelectionChange={handleAddonSelectionChange}
      />
      {showErrorMessageAttach ? <div ref={attachErrorMessageRef}>{renderErrorMessage()}</div> : null}
    </SlideoutLayoutSection>
  ) : null;

  const servicesModule = isServicesLoading ? (
    <LoadingSkeleton.Box height={{ xs: '320px', md: '220px' }} />
  ) : hasNonInstallServices && purchasedProductSku ? (
    <SlideoutLayoutSection key="services">
      <ProductService
        key={`${purchasedProductSku}-${isOpen}-${preSelectedOfferingId}`}
        productSku={purchasedProductSku}
        productServiceLink={cartProduct?.properties._serviceLink}
        productCartId={productCartId}
        preSelectedOfferingId={preSelectedOfferingId}
        gtmData={purchasedProduct.google_tag_manager}
        suggestions={nonInstallSuggestions}
        variant="slideout"
        shouldShowActionButtons={attachConfig?.shouldShowActionButtons}
        attachProductStatus={attachProductStatus}
        handleServiceStatus={handleServiceStatus}
      />
      {showErrorMessageAttach ? <div ref={attachErrorMessageRef}>{renderErrorMessage()}</div> : null}
    </SlideoutLayoutSection>
  ) : null;

  const productsModule = !isProductsModuleDisabled ? (
    <>
      {!isDesktop && isEnhancedGoogleRecommendationsEnabled ? (
        <SmartModuleWrapper
          settings={smartModuleSettings}
          location="productAttach"
          variant={isDesktop ? 'carousel' : 'list'}
          isMarketplaceProduct={isMarketplaceProduct}
        />
      ) : suggestions?.length ? (
        <SlideoutLayoutSection heading={title}>
          <EnhancedProductCarousel
            products={suggestions}
            numProducts={attachConfig?.numProductsPerNonBundleCategory}
            cardVariant={
              isEnhancedGoogleRecommendationsEnabled
                ? 'minified'
                : {
                    xs: attachConfig.productCardVariantMobile,
                    md: attachConfig.productCardVariantDesktop,
                  }
            }
            trackingData={{
              listName: `recommendations-popup${pageType}-${googleRecModelType?.toUpperCase() ?? 'FBT'}`,
              pageType,
              group: 'googleRelated',
              category: type ?? 'frequentlyBoughtTogether',
              googleRecModelType: smartModuleSettings?.googleRecModelType,
            }}
            testId="slideout-product-suggestions"
          />
        </SlideoutLayoutSection>
      ) : null}
    </>
  ) : null;

  const digitalAttachModule =
    isMsAttachedEnabled && shouldShowMicrosoft365Attach ? (
      <SlideoutLayoutSection>
        {msProductStatus === 'hidden' ? (
          <DigitalProductBlock
            {...ms365props}
            onAdd={() => {
              setIsMSProductAdded(true);
              ms365props.onAdd();
            }}
            bannerText={microsoft365AttachMessaging?.bannerText}
            informationMessage={microsoft365AttachMessaging?.informationMessage}
            shouldShowActionButtons={true}
            onRemove={() => handleServiceStatus('ms365')}
            productPrice={`$${microsoft365Product?.price?.toFixed(2)}`}
            isAddToCartLoading={isMSProductAdded}
          />
        ) : (
          <CollapsedContentBlock
            status={msProductStatus}
            title={ms365props.heading}
            price={`$${microsoft365Product.price.toFixed(2)}`}
            onClick={() => handleServiceStatus('ms365')}
          />
        )}

        {showErrorMessageMs ? <div ref={msErrorMessageRef}>{renderErrorMessage()}</div> : null}
      </SlideoutLayoutSection>
    ) : null;

  const slideoutModules = {
    services: [installsModule, servicesModule],
    products: productsModule,
    digitalAttach: digitalAttachModule,
  };
  return (
    <SmartModuleProvider
      sourceProducts={[currentProduct]}
      options={{
        smartModuleSettings: [smartModuleSettings],
        googleAcceptedDeviationPercentage: window.smartModuleSettings?.googleAcceptedDeviationPercentage,
        isEnhancedGoogleRecommendationsEnabled: !!isEnhancedGoogleRecommendationsEnabled,
        preservedProductCategories: window.smartModuleSettings?.preservedProductCategories,
      }}
    >
      <ShopifyProductActionsProvider>
        <TrackingProvider category="attach" context="slideout">
          <IdPrefixProvider prefix="slideout">
            <Slideout
              isOpen={!!(isOpen && hasProductsOrServices && !isLoading)}
              onClose={() => {
                tracker.trackClick({ label: 'Slideout close' });
                onClose();
              }}
              minWidth="800px"
              dataTestId="attach-slideout"
              backgroundColor="grey"
            >
              <SlideoutLayout
                heading={attachHeadingText}
                product={purchasedProduct}
                freeBonusProducts={freeBonusProducts}
                onCheckoutClick={handleCheckoutClick}
                onReviewCartClick={handleReviewCartClick}
                onContinueShoppingClick={handleContinueShopping}
                isCouponSurfacingEnabled={isCouponSurfacingEnabled}
                coupons={couponsWithExclusions}
                onHandleSelectCoupon={handleSelectCoupon}
                isPerks={isPerks}
                shouldShowButtons={!attachConfig?.shouldShowActionButtons}
                couponOffering={offering}
                isESimOnly={isESimOnly}
              >
                {attachSlideoutModuleOrder.map(module => (
                  <React.Fragment key={module}>{slideoutModules[module]}</React.Fragment>
                ))}
              </SlideoutLayout>
            </Slideout>
          </IdPrefixProvider>
        </TrackingProvider>
      </ShopifyProductActionsProvider>
    </SmartModuleProvider>
  );
};
